import React, { useState, useEffect, useRef } from "react";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import Link from "next/link";
import { Button } from "@mui/material";

// Infinite loop helper
const createInfiniteSlides = (items, slidesToShow) => {
  if (items.length <= slidesToShow) return items;
  const lastSlides = items.slice(-slidesToShow);
  const firstSlides = items.slice(0, slidesToShow);
  return [...lastSlides, ...items, ...firstSlides];
};

const EventsSlider = ({ events }) => {
  const [slidesToShow, setSlidesToShow] = useState(1);
  const [infiniteSlides, setInfiniteSlides] = useState([]);
  const [current, setCurrent] = useState(slidesToShow);

  const sliderRef = useRef(null);
  const intervalRef = useRef(null);
  const touchStartX = useRef(null);

  // 🔹 Touch swipe start
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    stopAutoplay();
  };

  // 🔹 Touch swipe end
  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const difference = touchStartX.current - touchEndX;

    if (difference > 50) {
      nextSlide();
    } else if (difference < -50) {
      prevSlide();
    }
  };

  // 🔹 Setup slides
  useEffect(() => {
    const updateSlides = () => {
      let num = 1; // always 1 card visible
      setSlidesToShow(num);
      setCurrent(num);
      setInfiniteSlides(createInfiniteSlides(events, num));
    };

    updateSlides();
    window.addEventListener("resize", updateSlides);
    return () => window.removeEventListener("resize", updateSlides);
  }, [events]);

  // 🔹 Handle infinite loop jump
  useEffect(() => {
    if (sliderRef.current) {
      if (current === infiniteSlides.length - slidesToShow) {
        setTimeout(() => {
          sliderRef.current.style.transition = "none";
          setCurrent(slidesToShow);
        }, 500);
      }
      if (current === 0) {
        setTimeout(() => {
          sliderRef.current.style.transition = "none";
          setCurrent(infiniteSlides.length - slidesToShow * 2);
        }, 500);
      }
    }
  }, [current, infiniteSlides, slidesToShow]);

  const startAutoplay = () => {
    stopAutoplay();
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => prev + 1);
      sliderRef.current.style.transition = "transform 0.5s ease-in-out";
    }, 5000);
  };

  const stopAutoplay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // 🔹 Manual controls
  const nextSlide = () => {
    setCurrent((prev) => prev + 1);
    sliderRef.current.style.transition = "transform 0.5s ease-in-out";
    stopAutoplay();
  };

  const prevSlide = () => {
    setCurrent((prev) => prev - 1);
    sliderRef.current.style.transition = "transform 0.5s ease-in-out";
    stopAutoplay();
  };

  const goToSlide = (pageIndex) => {
    setCurrent(pageIndex * slidesToShow + slidesToShow);
    sliderRef.current.style.transition = "transform 0.5s ease-in-out";
    stopAutoplay();
  };

  return (
    <div
      className="relative w-full overflow-hidden group"
      onMouseEnter={stopAutoplay}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slides */}
      <div
        ref={sliderRef}
        className="flex"
        style={{
          transform: `translateX(-${(100 / infiniteSlides.length) * current}%)`,
          width: `${infiniteSlides.length * (100 / slidesToShow)}%`,
          transition: "transform 0.5s ease-in-out",
        }}
      >
        {infiniteSlides.map((event, i) => {
          const isDisabled = event.isDisabled;

          return (
            <div
              key={i}
              className={`px-4 ${isDisabled ? "cursor-not-allowed" : "cursor-default"}`}
              style={{ width: `${100 / infiniteSlides.length}%` }}
            >
              <div
                className={`${
                  isDisabled
                    ? "bg-gray-200 text-gray-800 opacity-50 border border-gray-500"
                    : "bg-[#D9A443] hover:bg-[#E8BB68] text-black"
                } rounded-2xl shadow-xl p-6 h-[600px] md:h-[280px] overflow-y-auto flex flex-col justify-between text-left transition-all`}
              >
                <div>
                  <h3 className="text-md md:text-2xl font-bold mb-2">
                    {event.title}
                  </h3>
                  <p className="text-sm my-3">{event.description}</p>
                </div>
                {event.button && (
                  <div className="m-auto">
                    <Link href={event.button.link}>
                      <Button
                        variant="outlined"
                        sx={{
                          color: "black", // text color
                          borderColor: "black", // border color
                          "&:hover": {
                            borderColor: "white", // keep border black on hover
                            backgroundColor: "transparent", // optional: prevent default hover bg
                          },
                        }}
                      >
                        {event.button.title}
                      </Button>
                    </Link>
                  </div>
                )}
                <div className="text-sm">
                  <p className="font-semibold text-md md:text-lg mb-1">
                    📅 {event.date}
                  </p>
                  <p className="mt-1 text-md md:text-lg">☎ {event.contact}</p>
                  <p>📍 {event.location}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Arrows */}
      <button
        onClick={prevSlide}
        disabled={events.every((e) => e.isDisabled)} // disable nav if all disabled
        className="absolute top-1/2 -left-2 -translate-y-1/2 p-2 bg-white rounded-full shadow-md text-2xl z-10 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <IoIosArrowBack />
      </button>
      <button
        onClick={nextSlide}
        disabled={events.every((e) => e.isDisabled)}
        className="absolute top-1/2 -right-2 -translate-y-1/2 p-2 bg-white rounded-full shadow-md text-2xl z-10 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-80 disabled:cursor-not-allowed"
      >
        <IoIosArrowForward />
      </button>

      {/* Dots */}
      <div className="flex justify-center my-8 space-x-2">
        {[...Array(Math.ceil(events.length / slidesToShow))].map((_, i) => (
          <button
            key={i}
            onClick={() => goToSlide(i)}
            disabled={events
              .slice(i, i + slidesToShow)
              .every((e) => e.isDisabled)}
            className={`w-3 h-3 rounded-full transition-colors ${
              Math.floor((current - slidesToShow) / slidesToShow) === i
                ? "bg-black"
                : "bg-gray-400"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          />
        ))}
      </div>
    </div>
  );
};

export default EventsSlider;
